import React from 'react';
import { MessageSquare, ChevronUp, ChevronDown, ListChecks } from 'lucide-react';
import { DesignDataCell } from '@/components/result/DesignDataCell';
import { RESULT_DESIGN_PLACEHOLDER } from '@/constants/resultDesignPlaceholder';
import { ScenarioResultBadge } from './ScenarioResultBadge';
import { useTranslation } from 'react-i18next';
import type { ChecklistItem, EvaluationType } from '@/types/admin';

const PL = RESULT_DESIGN_PLACEHOLDER;

function EvaluationTypePlaceholderRows({ type, rowKeyPrefix }: { type: EvaluationType; rowKeyPrefix: string }) {
    const commonTail = () => (
        <>
            <td className="px-4 py-3 font-semibold border-s border-gray-100 bg-gray-50/10 align-top">
                <DesignDataCell hint="획득·점수" align="center" />
            </td>
            <td className="px-4 py-3 align-top">
                <div className="inline-flex w-full flex-col items-center">
                    <span className="inline-block w-20 py-1 rounded text-xs font-semibold bg-amber-50/90 text-amber-800 border border-amber-200/90">
                        {PL}
                    </span>
                    <span className="text-[10px] text-slate-400 font-normal normal-case leading-tight mt-1">성공·부분·오류</span>
                </div>
            </td>
            <td className="px-4 py-3 align-top">
                <div className="inline-flex w-full flex-col items-center">
                    <ChevronDown className="mx-auto text-slate-400" size={18} aria-hidden />
                    <span className="text-[10px] text-slate-400 font-normal normal-case leading-tight mt-1">피드백(펼침)</span>
                </div>
            </td>
        </>
    );
    const rowClass = (r: number) =>
        (r + 1) % 2 === 0
            ? 'bg-slate-50/60 border-b border-slate-100/90'
            : 'bg-white border-b border-slate-100/90';
    return (
        <>
            {type === 'performance' &&
                [0, 1, 2].map((r) => (
                    <tr className={rowClass(r)} key={`${rowKeyPrefix}-p-${r}`}>
                        <td className="px-4 py-3 text-center text-gray-600 font-medium normal-case">{(r + 1).toString()}</td>
                        <td className="px-4 py-3">
                            <DesignDataCell hint="이벤트" align="left" />
                        </td>
                        <td className="px-4 py-3">
                            <div className="flex flex-wrap items-start gap-3">
                                <div className="shrink-0 text-center">
                                    <span className="inline-block w-20 text-center px-2 py-0.5 rounded text-sm font-medium border border-blue-200/90 bg-blue-50/80 text-blue-800">
                                        {PL}
                                    </span>
                                    <div className="text-[10px] text-slate-400 font-normal normal-case leading-tight mt-1">태스크·유형</div>
                                </div>
                                <div className="min-w-0 flex-1">
                                    <DesignDataCell hint="태스크·명" align="left" />
                                </div>
                            </div>
                        </td>
                        <td className="px-4 py-3">
                            <DesignDataCell hint="지문·상세" align="left" />
                        </td>
                        {commonTail()}
                    </tr>
                ))}
            {type === 'assessment' &&
                [0, 1, 2].map((r) => (
                    <tr className={rowClass(r)} key={`${rowKeyPrefix}-a-${r}`}>
                        <td className="px-4 py-3 text-center text-gray-600 font-medium normal-case">{(r + 1).toString()}</td>
                        <td className="px-4 py-3">
                            <DesignDataCell hint="평가·항목" align="left" />
                        </td>
                        <td className="px-4 py-3">
                            <DesignDataCell hint="문구·지문" align="left" />
                        </td>
                        <td className="px-4 py-3 text-center">
                            <span className="font-semibold text-blue-800 bg-blue-50/90 px-1.5 py-0.5 rounded text-sm border border-blue-100/90 inline-block min-w-[80px]">
                                {PL}
                            </span>
                            <div className="text-[10px] text-slate-400 font-normal normal-case leading-tight mt-1">정답</div>
                        </td>
                        <td className="px-4 py-3 text-center">
                            <span className="text-slate-800 font-semibold bg-slate-50/90 px-1.5 py-0.5 rounded-lg border border-slate-200/90 inline-block min-w-[80px]">
                                {PL}
                            </span>
                            <div className="text-[10px] text-slate-400 font-normal normal-case leading-tight mt-1">응답(선택)</div>
                        </td>
                        {commonTail()}
                    </tr>
                ))}
            {type === 'equipment' &&
                [0, 1, 2].map((r) => (
                    <tr className={rowClass(r)} key={`${rowKeyPrefix}-e-${r}`}>
                        <td className="px-4 py-3 text-center text-gray-600 font-medium normal-case">{(r + 1).toString()}</td>
                        <td className="px-4 py-3">
                            <DesignDataCell hint="도구" align="left" />
                        </td>
                        <td className="px-4 py-3">
                            <div className="space-y-2">
                                <DesignDataCell hint="명(상)" align="left" />
                                <DesignDataCell hint="부(하단)" align="left" />
                            </div>
                        </td>
                        <td className="px-4 py-3 text-center">
                            <span className="text-[10px] font-semibold px-2 py-1 rounded bg-orange-50/90 text-orange-800 border border-orange-200/80 uppercase">Req</span>
                            <div className="text-[10px] text-slate-400 font-normal normal-case leading-tight mt-1.5">필수</div>
                        </td>
                        {commonTail()}
                    </tr>
                ))}
            {type === 'procedure' &&
                [0, 1, 2].map((r) => (
                    <tr className={rowClass(r)} key={`${rowKeyPrefix}-pr-${r}`}>
                        <td className="px-4 py-3 text-center text-gray-600 font-medium normal-case">{(r + 1).toString()}</td>
                        <td className="px-4 py-3 text-sm">
                            <DesignDataCell hint="절차·이름" align="left" />
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                            <DesignDataCell hint="가이드" align="left" />
                        </td>
                        {commonTail()}
                    </tr>
                ))}
        </>
    );
}

function EvaluationThead({ type, hideScoreColumn, hideTaskColumn, hideFeedbackColumn, hideDescriptionColumn }: { type: EvaluationType; hideScoreColumn?: boolean; hideTaskColumn?: boolean; hideFeedbackColumn?: boolean; hideDescriptionColumn?: boolean }) {
    return (
        <thead className="text-sm text-gray-700 uppercase bg-gray-50 border-b border-gray-200">
            <tr>
                <th className="px-4 py-3 w-16 text-center">NO</th>
                {type === 'performance' && (
                    <>
                        <th className="px-4 py-3 w-40">평가항목</th>
                        {!hideTaskColumn && <th className="px-4 py-3 w-64">태스크</th>}
                        {!hideDescriptionColumn && <th className="px-4 py-3">내용</th>}
                    </>
                )}
                {type === 'assessment' && (
                    <>
                        <th className="px-4 py-3 w-40">평가 항목</th>
                        {!hideDescriptionColumn && <th className="px-4 py-3">내용</th>}
                        <th className="px-4 py-3 w-24 text-center">정답</th>
                        <th className="px-4 py-3 w-24 text-center">선택</th>
                    </>
                )}
                {type === 'equipment' && (
                    <>
                        <th className="px-4 py-3 w-48">도구 명칭</th>
                        <th className="px-4 py-3">분류/상태</th>
                        <th className="px-4 py-3 w-28 text-center">필수 여부</th>
                    </>
                )}
                {type === 'procedure' && (
                    <>
                        <th className="px-4 py-3 w-56">절차명</th>
                        <th className="px-4 py-3">수행지침</th>
                    </>
                )}
                <th className="px-4 py-3 w-20 text-center">결과</th>
                {!hideScoreColumn && (
                    <th className="px-4 py-3 w-28 text-center border-s border-gray-100 bg-gray-50/30">점수</th>
                )}
                {!hideFeedbackColumn && (
                    <th className="px-4 py-3 w-20 text-center">코멘트</th>
                )}
            </tr>
        </thead>
    );
}

interface EvaluationTableProps {
    title: string;
    type: EvaluationType;
    items: ChecklistItem[];
    expandedFeedback: Set<string>;
    onToggleFeedback: (id: string) => void;
    hideScoreColumn?: boolean;
    hideTaskColumn?: boolean;
    useSimpleResultBadge?: boolean;
    hideFeedbackColumn?: boolean;
    useFeedbackAsDescription?: boolean;
    hideDescriptionColumn?: boolean;
    useActedPassLogic?: boolean;
}

export const EvaluationTable: React.FC<EvaluationTableProps> = ({
    title,
    type,
    items,
    expandedFeedback,
    onToggleFeedback,
    hideScoreColumn = false,
    hideTaskColumn = false,
    useSimpleResultBadge = false,
    hideFeedbackColumn = false,
    useFeedbackAsDescription = false,
    hideDescriptionColumn = false,
    useActedPassLogic = false,
}) => {
    const { t: tScenario } = useTranslation('scenario');
    
    // S12 등 번역 키를 실제 텍스트로 변환하는 함수
    const translateIfKey = (text: string): string => {
        if (!text) return text;
        // S12로 시작하는 번역 키면 번역 시도
        if (text.startsWith('S12.') || text.startsWith('S12_')) {
            const normalizedKey = text.replace(/_/g, '.');
            const translated = tScenario(normalizedKey);
            // 번역이 키 그대로 반환되면 원본 사용, 아니면 번역 사용
            return translated !== normalizedKey ? translated : text;
        }
        return text;
    };
    
    const getResultBadge = (status: string | undefined, acted?: boolean, pass?: boolean) => {
        // ls 컨텐츠에서만 acted와 pass 필드 기반 판별 사용
        if (useActedPassLogic && acted !== undefined && pass !== undefined) {
            if (!acted) {
                // 미수행
                return (
                    <span className="inline-block w-20 py-1 rounded text-xs font-semibold bg-gray-100 text-gray-600 border border-gray-200">
                        미수행
                    </span>
                );
            }
            if (pass) {
                // 정답
                return (
                    <span className="inline-block w-20 py-1 rounded text-xs font-semibold bg-green-100 text-green-700 border border-green-200">
                        정답
                    </span>
                );
            }
            // 오답
            return (
                <span className="inline-block w-20 py-1 rounded text-xs font-semibold bg-red-100 text-red-700 border border-red-200">
                    오답
                </span>
            );
        }
        
        // 기존 status 기반 로직
        const normalizedStatus = status ?? 'fail';
        
        if (useSimpleResultBadge) {
            // ls처럼 간단한 정답/오답 배지
            const isCorrect = normalizedStatus === 'success';
            return (
                <ScenarioResultBadge
                    ok={isCorrect}
                    labelCorrect={tScenario('gcsEvaluationResult.resultCorrect')}
                    labelWrong={tScenario('gcsEvaluationResult.resultWrong')}
                />
            );
        }
        // 기존 SUCCESS/PARTIAL/FAIL 배지
        return (
            <span className={`inline-block w-20 py-1 rounded text-xs font-semibold ${normalizedStatus === 'success' ? 'bg-green-100 text-green-700' :
                normalizedStatus === 'partial' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-red-100 text-red-700'
                }`}>
                {normalizedStatus === 'success' ? 'SUCCESS' :
                    normalizedStatus === 'partial' ? 'PARTIAL' : 'FAIL'}
            </span>
        );
    };
    
    if (items.length === 0) {
        return (
            <div className="space-y-4">
                <div className="flex items-center justify-between">
                    <h3 className="text-lg md:text-xl font-bold text-gray-900 flex items-center gap-2.5 min-w-0">
                        <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-violet-200/60 border border-violet-200/90 shrink-0">
                            <ListChecks size={20} className="text-violet-800" />
                        </div>
                        <span className="truncate min-w-0">{title}</span>
                    </h3>
                </div>
                <div
                    className="overflow-hidden rounded-xl border border-slate-200/90 border-s-4 border-s-violet-500/45 shadow-sm bg-white"
                    role="presentation"
                    aria-hidden
                >
                    <table className="w-full text-base text-start border-collapse">
                        <EvaluationThead type={type} hideScoreColumn={hideScoreColumn} hideTaskColumn={hideTaskColumn} hideFeedbackColumn={hideFeedbackColumn} hideDescriptionColumn={hideDescriptionColumn} />
                        <tbody className="divide-y divide-gray-200 text-base normal-case">
                            <EvaluationTypePlaceholderRows type={type} rowKeyPrefix="design" />
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <h3 className="text-lg md:text-xl font-bold text-gray-900 flex items-center gap-2.5">
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-slate-200/60 border border-slate-200/80">
                        <ListChecks size={20} className="text-slate-600" />
                    </div>
                    {title}
                </h3>
            </div>

            <div className="overflow-hidden rounded-xl border border-slate-200/90 shadow-sm bg-white">
                <table className="w-full text-base text-start border-collapse">
                    <EvaluationThead type={type} hideScoreColumn={hideScoreColumn} hideTaskColumn={hideTaskColumn} hideFeedbackColumn={hideFeedbackColumn} hideDescriptionColumn={hideDescriptionColumn} />
                    <tbody className="divide-y divide-gray-200">
                        {items.map((item, idx) => (
                            <React.Fragment key={item.id}>
                                <tr className={`hover:bg-gray-50 transition-colors ${!useFeedbackAsDescription && item.feedback && item.feedback.trim() !== '' ? 'cursor-pointer' : ''} break-inside-avoid ${expandedFeedback.has(item.id) ? 'bg-gray-50' : ''}`}
                                    onClick={() => !useFeedbackAsDescription && item.feedback && item.feedback.trim() !== '' && onToggleFeedback(item.id)}>
                                    <td className="px-4 py-3 text-center text-gray-500 font-medium">{idx + 1}</td>

                                    {type === 'performance' && (
                                        <>
                                            <td className="px-4 py-3 font-medium text-gray-900 text-sm text-gray-500 leading-relaxed">
                                                {useFeedbackAsDescription ? translateIfKey(item.feedback || item.description) : translateIfKey(item.eventName)}
                                            </td>
                                            {!hideTaskColumn && (
                                                <td className="px-4 py-3">
                                                    <div className="flex items-center gap-3">
                                                        <span className={`inline-block w-20 text-center px-2 py-0.5 rounded text-sm font-medium border shrink-0 ${item.taskType === 'Must-not' ? 'text-red-600 bg-red-50 border-red-200' :
                                                            item.taskType === 'Decision' ? 'text-purple-600 bg-purple-50 border-purple-200' :
                                                                'text-blue-600 bg-blue-50 border-blue-200'
                                                            }`}>
                                                            {item.taskType}
                                                        </span>
                                                        <span className="font-medium text-gray-900">{translateIfKey(item.taskName)}</span>
                                                    </div>
                                                </td>
                                            )}
                                            {!hideDescriptionColumn && (
                                                <td className="px-4 py-3 text-sm text-gray-500 leading-relaxed whitespace-pre-wrap">
                                                    {translateIfKey(item.description)}
                                                </td>
                                            )}
                                        </>
                                    )}

                                    {type === 'assessment' && (
                                        <>
                                            <td className="px-4 py-3 font-medium text-gray-900">
                                                {useFeedbackAsDescription ? translateIfKey(item.feedback || item.description) : translateIfKey(item.taskName)}
                                            </td>
                                            {!hideDescriptionColumn && (
                                                <td className="px-4 py-3 text-sm text-gray-500 whitespace-pre-wrap">
                                                    {translateIfKey(item.description)}
                                                </td>
                                            )}
                                            <td className="px-4 py-3 text-center">
                                                <span className="font-semibold text-blue-600 bg-blue-50 px-1 py-0.5 rounded text-sm border border-blue-100 inline-block min-w-[80px]">
                                                    {item.correctValue || '-'}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-center">
                                                <span className="text-gray-700 font-semibold bg-gray-50 px-1 py-0.5 rounded-lg border border-gray-100 inline-block min-w-[80px]">
                                                    {item.value || '-'}
                                                </span>
                                            </td>
                                        </>
                                    )}

                                    {type === 'equipment' && (
                                        <>
                                            <td className="px-4 py-3 font-medium text-gray-900">{translateIfKey(item.taskName)}</td>
                                            <td className="px-4 py-3">
                                                <div className="text-gray-700 font-semibold">{item.value || '-'}</div>
                                                <div className="text-xs text-gray-400 mt-1 whitespace-pre-wrap">{translateIfKey(item.description)}</div>
                                            </td>
                                            <td className="px-4 py-3 text-center">
                                                <span className="text-[10px] font-semibold px-2 py-1 rounded bg-orange-50 text-orange-600 border border-orange-200 uppercase">Required</span>
                                            </td>
                                        </>
                                    )}

                                    {type === 'procedure' && (
                                        <>
                                            <td className="px-4 py-3 font-medium text-gray-900 text-sm">{translateIfKey(item.taskName)}</td>
                                            <td className="px-4 py-3 text-gray-600 font-normal leading-relaxed whitespace-pre-wrap">{translateIfKey(item.description)}</td>
                                        </>
                                    )}

                                    <td className="px-4 py-3 text-center">
                                        {getResultBadge(item.status, item.acted, item.pass)}
                                    </td>
                                    {!hideScoreColumn && (
                                        <td className="px-4 py-3 text-center font-semibold border-s border-gray-100 bg-gray-50/10">
                                            <span
                                                className={`text-sm ${
                                                    item.score < 0
                                                        ? 'text-red-500'
                                                        : item.rowScoreUnspecified
                                                          ? 'text-gray-400'
                                                          : 'text-gray-900'
                                                }`}
                                            >
                                                {item.rowScoreUnspecified
                                                    ? '—'
                                                    : item.score > 0
                                                      ? `+${item.score}`
                                                      : item.score}
                                            </span>
                                        </td>
                                    )}
                                    {!hideFeedbackColumn && !useFeedbackAsDescription && (
                                        <td className="px-4 py-3 text-center">
                                            {item.feedback && item.feedback.trim() !== '' ? (
                                                <div className={`h-10 w-10 p-0 inline-flex items-center justify-center rounded-md transition-all cursor-pointer ${expandedFeedback.has(item.id) ? 'bg-gray-200' : ''}`}>
                                                    {expandedFeedback.has(item.id) ? (
                                                        <ChevronUp size={16} className="text-gray-600" />
                                                    ) : (
                                                        <ChevronDown size={16} className="text-gray-500" />
                                                    )}
                                                </div>
                                            ) : (
                                                <span className="text-gray-300 text-sm">—</span>
                                            )}
                                        </td>
                                    )}
                                </tr>
                                {!hideFeedbackColumn && !useFeedbackAsDescription && expandedFeedback.has(item.id) && item.feedback && item.feedback.trim() !== '' && (
                                    <tr className="bg-gray-50 animate-in fade-in slide-in-from-top-1 duration-200">
                                        <td colSpan={10} className="px-4 pb-2 pt-0 border-b border-gray-100">
                                            <div className="ms-12 me-4 bg-white p-4 rounded-lg shadow-sm mt-2">
                                                <div className="flex items-start gap-3">
                                                    <div className="mt-0.5 p-1.5 bg-indigo-50 rounded-full text-indigo-600">
                                                        <MessageSquare size={16} />
                                                    </div>
                                                    <div className="flex-1">
                                                        <p className="text-gray-600 text-sm leading-relaxed whitespace-pre-wrap">
                                                            {translateIfKey(item.feedback)}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};
